import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./LifelinePaywall.module.scss"

/**
 * Lifeline — an EXPIRATION / win-back paywall (paywall type randomly rolled this
 * run — rolled *Expiration*). Built to the active Slack standing directive
 * ("build paywalls centered around loss aversion, use fear as the driving factor.
 * Include some amazing animations, highlight our feature list. Target new users"):
 * the fear is the honest fragility of a **single copy** on a single phone, and
 * the relief is a woven **safety line** (a private, backed-up second copy).
 *
 * The mechanic is a living **braided lifeline**. Your memories are held at the
 * top; a private-backup node anchors the bottom; between them runs a cord. While
 * your trial was on, the cord was a taut two-strand **braid** (2 copies). The
 * trial just ended, so on load one strand is honestly *snipped* — the braid
 * unravels to a single, faintly *trembling* thread (protection paused · 1 copy),
 * and the feature tags it carries dim to amber. Nothing is ever deleted: the
 * memories stay put; only the SECOND copy is what lapses. Press the CTA and the
 * second strand **weaves back up** the cord, the two braid tight and steady, a
 * lock seals mid-span, and every protection tag lights blue again (2 copies).
 *
 * Loss aversion, kept honest: the peril shown is the *real world* — a lost,
 * dropped or stolen phone — never the app taking anything away. Copy is explicit
 * that photos are always yours and never deleted; the safety line only keeps a
 * second private copy so a bad day for your phone is never a bad day for your
 * memories. No countdown, no fake scarcity, honest savings from real prices.
 *
 * Divergence: signature is a two-strand SVG cord that braids/unravels with real
 * pointer tension — not the expiration family's tide (Tideline), lift-the-cover
 * wall (Homecoming), systems console (Standby) or cold ice (Deep Freeze). Value
 * frame is *single-copy fragility → redundancy*; register is *taut vigilance →
 * relief*. Yearly is the featured, most-prominent price; monthly is one demoted
 * line. Self-contained on the built-in debug product data + bundled nimbus/
 * photos; no native injection.
 */

type Tier = "premium" | "ultimate"

// Real memories from the bundled set — the cluster the lifeline holds.
const MEMORIES = ["mem6.jpg", "mem1.jpg", "mem12.jpg", "mem9.jpg"]

// The protections the safety line carries — real Premium/Ultimate capabilities,
// placed down the cord so the line visibly "carries" them.
interface Guard {
  label: string
  detail: string
  icon: IconName
  ultimate?: boolean
}
const GUARDS: Guard[] = [
  {
    label: "Private cloud backup",
    detail: "Every new photo, copied off your phone",
    icon: "cloud",
  },
  {
    label: "Full-quality originals",
    detail: "Kept full-resolution, never shrunk",
    icon: "image",
  },
  {
    label: "On every device",
    detail: "In sync wherever you sign in",
    icon: "devices",
  },
  {
    label: "Break-in alerts",
    detail: "Snaps anyone who tries a wrong PIN",
    icon: "alert",
  },
]

type IconName =
  | "lock"
  | "cloud"
  | "image"
  | "devices"
  | "alert"
  | "arrow"
  | "close"
  | "check"
  | "shield"
  | "spark"

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const Icon = ({ name }: { name: IconName }) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "cloud":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 18h10a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6.5 9.2 3.9 3.9 0 0 0 7 18Z"
            fill="currentColor"
          />
          <path
            d="M12 15.5v-5m0 0 2 2m-2-2-2 2"
            stroke="var(--lf-node)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "image":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="3.5"
            y="5"
            width="17"
            height="14"
            rx="3"
            fill="currentColor"
          />
          <circle cx="9" cy="10" r="1.7" fill="var(--lf-node)" />
          <path
            d="m5 17 4-4 3.2 3 2.3-2.3L19 17.5"
            stroke="var(--lf-node)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "devices":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="2.5"
            y="5.5"
            width="13"
            height="10"
            rx="2"
            fill="currentColor"
          />
          <rect
            x="16"
            y="8.5"
            width="5.5"
            height="10"
            rx="1.6"
            fill="currentColor"
          />
          <path
            d="M6 19h6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
    case "alert":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3.6 20.5 19a1 1 0 0 1-.87 1.5H4.37A1 1 0 0 1 3.5 19L12 3.6Z"
            fill="currentColor"
          />
          <path
            d="M12 9.5v4.2"
            stroke="var(--lf-node)"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.7" r="1.1" fill="var(--lf-node)" />
        </svg>
      )
    case "shield":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.6 20 5.4v6c0 4.7-3.2 8.4-8 10-4.8-1.6-8-5.3-8-10v-6L12 2.6Z"
            fill="currentColor"
          />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "spark":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.5c.6 4.7 2.3 6.4 7 7-4.7.6-6.4 2.3-7 7-.6-4.7-2.3-6.4-7-7 4.7-.6 6.4-2.3 7-7Z"
            fill="currentColor"
          />
        </svg>
      )
  }
}

// ---- Cord geometry ---------------------------------------------------------
// The cord runs top→bottom in a 100×220 viewBox. Two strands weave around the
// centre line; `weave` (0=single trembling thread … 1=tight braid) and a time
// value (for the nervous tremble of a lone strand) fully describe it.
const VB_W = 100
const VB_H = 220
const CORD_TOP = 20
const CORD_BOT = 200
const SAMPLES = 60

function strandPoints(strand: 0 | 1, weave: number, time: number): string {
  const braidAmp = 8 * weave
  const trembleAmp = (1 - weave) * 3.4
  const phase = strand === 0 ? 0 : Math.PI
  let d = ""
  for (let i = 0; i <= SAMPLES; i++) {
    const p = i / SAMPLES
    const y = CORD_TOP + (CORD_BOT - CORD_TOP) * p
    // taper the weave near the two anchors so strands meet cleanly
    const taper = Math.sin(p * Math.PI)
    const cross = Math.sin(p * Math.PI * 6 + phase) * braidAmp * taper
    // only the surviving lone strand trembles, and only while unwoven
    const tremble =
      strand === 0 ? Math.sin(p * 20 + time * 5.5) * trembleAmp * taper : 0
    const x = VB_W / 2 + cross + tremble
    d += `${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)} `
  }
  return d
}

const LifelinePaywall = () => {
  const { state, actions } = useAppState()

  const ultimate = state.productData?.ultimate
  const premium = state.productData?.premium
  const locale =
    premium?.yearlyProduct?.priceLocale ?? ultimate?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null ? formatCurrency(v, locale) : fallback

  const plans = useMemo(
    () => ({
      premium: {
        yearly: premium?.yearlyProduct?.price ?? 35.99,
        monthly: premium?.monthlyProduct?.price ?? 13.99,
        product: premium?.yearlyProduct,
        monthlyProduct: premium?.monthlyProduct,
      },
      ultimate: {
        yearly: ultimate?.yearlyProduct?.price ?? 119.99,
        monthly: ultimate?.monthlyProduct?.price ?? 19.99,
        product: ultimate?.yearlyProduct,
        monthlyProduct: ultimate?.monthlyProduct,
      },
    }),
    [premium, ultimate],
  )

  const [tier, setTier] = useState<Tier>("premium")
  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")

  // `weave` is the single source of truth for the whole cord.
  const [weave, setWeave] = useState(1)
  const weaveRef = useRef(1)
  const [armed, setArmed] = useState(true)
  const rafRef = useRef(0)
  // a continuously advancing clock drives the tremble of a lone strand
  const [clock, setClock] = useState(0)
  const clockRef = useRef(0)
  const clockRafRef = useRef(0)
  const tugRef = useRef(0)
  const [tug, setTug] = useState(0)
  const reducedMotion = useRef(false)

  const writeWeave = (v: number) => {
    weaveRef.current = v
    setWeave(v)
  }

  const animateWeaveTo = useCallback((target: number, dur = 900) => {
    cancelAnimationFrame(rafRef.current)
    if (reducedMotion.current) {
      writeWeave(target)
      setArmed(target > 0.5)
      return
    }
    const from = weaveRef.current
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur)
      writeWeave(lerp(from, target, easeInOutCubic(t)))
      setArmed(weaveRef.current > 0.5)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  // Tint the OS top inset to the dark instrument ground; drop the footer inset
  // band so the page's own gradient + legal row run edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#0e1424")
    actions.setIgnoreFooterInset(true)
  }, [actions])

  // Continuous clock for the tremble (kept light: only advances a scalar).
  useEffect(() => {
    reducedMotion.current =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true

    const start = performance.now()
    const tick = (now: number) => {
      clockRef.current = (now - start) / 1000
      setClock(clockRef.current)
      // relax any tug back toward rest
      if (tugRef.current > 0.001) {
        tugRef.current *= 0.9
        setTug(tugRef.current)
      }
      clockRafRef.current = requestAnimationFrame(tick)
    }
    if (!reducedMotion.current)
      clockRafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(clockRafRef.current)
  }, [])

  // Auto-demo on load: hold the braid a beat (what the trial gave you), then
  // honestly snip a strand so it unravels to the single trembling thread — the
  // paused state the CTA restores.
  useEffect(() => {
    if (reducedMotion.current) {
      writeWeave(0)
      setArmed(false)
      return
    }
    writeWeave(1)
    setArmed(true)
    const t1 = setTimeout(() => animateWeaveTo(0, 1150), 950)
    return () => {
      clearTimeout(t1)
      cancelAnimationFrame(rafRef.current)
    }
  }, [animateWeaveTo])

  // Tug the cord — a visceral reminder that a lone strand is all that's holding.
  const onCordPress = useCallback(() => {
    if (weaveRef.current > 0.5) return
    tugRef.current = 1
    setTug(1)
  }, [])

  const active = plans[tier]
  const yearlyPrice = money(
    active.yearly,
    tier === "premium" ? "$35.99" : "$119.99",
  )
  const perMonthOfYear = money(
    active.yearly / 12,
    tier === "premium" ? "$2.99" : "$9.99",
  )
  const monthlyPrice = money(
    active.monthly,
    tier === "premium" ? "$13.99" : "$19.99",
  )
  const savePercent = Math.max(
    0,
    Math.round((1 - active.yearly / (active.monthly * 12)) * 100),
  )

  const selectedProduct =
    billing === "yearly" ? active.product : active.monthlyProduct

  const buy = useCallback(() => {
    // Re-weave the safety line, then hand off to the native purchase.
    animateWeaveTo(1, 820)
    if (selectedProduct) native.subscribe(selectedProduct)
  }, [animateWeaveTo, selectedProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  // ---- Derived visuals -----------------------------------------------------
  const w = clamp01(weave)
  const time = clock + tug * 4
  const trembleWeave = clamp01(w - tug * 0.55) // a tug briefly "unwinds" it
  const strand0 = strandPoints(0, trembleWeave, time)
  const strand1 = strandPoints(1, trembleWeave, time)
  const cordWidth = lerp(2.1, 3.4, w)
  const memorySettle = easeOutCubic(w)

  return (
    <div
      className={styles.page}
      data-armed={armed}
      style={
        {
          "--w": w,
          "--settle": memorySettle,
        } as React.CSSProperties
      }
    >
      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Icon name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Icon name="lock" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Don&rsquo;t leave your memories
          <br />
          hanging by a <span className={styles.hlThread}>single thread</span>.
        </h1>
        <p className={styles.sub}>
          Your free trial just ended. Every photo is still safe in your vault —
          but new memories have stopped getting a second, backed-up copy.
        </p>
      </header>

      <div className={styles.statusRow}>
        <span className={styles.status}>
          <span className={styles.statusDot} aria-hidden="true" />
          {armed ? "Protected · 2 copies" : "Protection paused · 1 copy"}
        </span>
      </div>

      {/* The signature: memories held by a lifeline that braids or unravels. */}
      <section className={styles.stage}>
        <div className={styles.spine}>
          {/* memories cluster at the top anchor */}
          <div className={styles.holder}>
            <div className={styles.memories}>
              {MEMORIES.map((f, i) => (
                <span className={styles.memory} data-i={i} key={f}>
                  <img
                    src={`${process.env.PUBLIC_URL}/nimbus/${f}`}
                    alt=""
                    draggable={false}
                    loading="eager"
                  />
                </span>
              ))}
            </div>
            <span className={styles.holderTag}>your memories</span>
          </div>

          {/* the cord */}
          <button
            className={styles.cord}
            onPointerDown={onCordPress}
            aria-label={
              armed
                ? "Your safety line is woven"
                : "Tap to feel the tension on the single thread"
            }
          >
            <svg
              className={styles.cordSvg}
              viewBox={`0 0 ${VB_W} ${VB_H}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="lfStrandA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="var(--lf-cord-top)" />
                  <stop offset="1" stopColor="var(--lf-cord-bot)" />
                </linearGradient>
              </defs>
              {/* strand 1 — the second copy, fades + tightens in as it weaves */}
              <path
                className={styles.strandTwo}
                d={strand1}
                stroke="var(--lf-strand-two)"
                strokeWidth={cordWidth}
                strokeLinecap="round"
                fill="none"
                vectorEffect="non-scaling-stroke"
                style={{ opacity: 0.12 + 0.88 * easeOutCubic(w) }}
              />
              {/* strand 0 — the copy on this phone, always present */}
              <path
                className={styles.strandOne}
                d={strand0}
                stroke="url(#lfStrandA)"
                strokeWidth={cordWidth}
                strokeLinecap="round"
                fill="none"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* the seal that clasps mid-span when the braid is tight */}
            <span className={styles.seal} data-on={w > 0.6} aria-hidden="true">
              <Icon name="lock" />
            </span>

            {!armed && (
              <span className={styles.snip} aria-hidden="true">
                strand cut
              </span>
            )}
          </button>

          {/* the private-backup node at the bottom anchor */}
          <div className={styles.node} data-on={armed}>
            <span className={styles.nodeIcon}>
              <Icon name="shield" />
            </span>
            <span className={styles.nodeText}>
              <strong>Private backup</strong>
              <span>
                {armed ? "second copy, encrypted" : "waiting to reconnect"}
              </span>
            </span>
          </div>
        </div>

        {/* what the line carries — the real protections, highlighted */}
        <ul className={styles.guards}>
          {GUARDS.map((g) => (
            <li className={styles.guard} key={g.label} data-on={armed}>
              <span className={styles.guardIcon}>
                <Icon name={g.icon} />
              </span>
              <span className={styles.guardText}>
                <strong>{g.label}</strong>
                <span>{g.detail}</span>
              </span>
              <span className={styles.guardMark} aria-hidden="true">
                <Icon name={armed ? "check" : "close"} />
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* reassurance — the spine of App-Store compliance, said plainly */}
      <p className={styles.reassure}>
        <Icon name="shield" />
        Your photos are always yours. Nothing is ever deleted — the safety line
        just keeps a second private copy so a lost or broken phone never means a
        lost memory.
      </p>

      {/* pricing — yearly featured; monthly demoted to a single line */}
      <section className={styles.pricing}>
        <div
          className={styles.tierSwitch}
          role="tablist"
          aria-label="Choose a plan"
        >
          <button
            role="tab"
            aria-selected={tier === "premium"}
            className={`${styles.tierBtn} ${tier === "premium" ? styles.tierOn : ""}`}
            onClick={() => setTier("premium")}
          >
            Premium
          </button>
          <button
            role="tab"
            aria-selected={tier === "ultimate"}
            className={`${styles.tierBtn} ${styles.tierUlt} ${
              tier === "ultimate" ? styles.tierOn : ""
            }`}
            onClick={() => setTier("ultimate")}
          >
            <Icon name="spark" />
            Ultimate · 1 TB
          </button>
        </div>

        <button
          className={`${styles.yearCard} ${billing === "yearly" ? styles.yearOn : ""}`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearBadge}>
            BEST VALUE · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>
              {tier === "premium" ? "Premium" : "Ultimate"} · Yearly
            </span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>{perMonthOfYear}/mo, billed yearly</span>
            <span>Protection stays on all year</span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            billing === "monthly" ? styles.monthOn : ""
          }`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>Weave my safety line back on</span>
          <Icon name="arrow" />
        </button>

        <p className={styles.fine}>
          {billing === "yearly"
            ? `${yearlyPrice} per year`
            : `${monthlyPrice} per month`}
          . Resumes protection from today · auto-renews until canceled · cancel
          anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default LifelinePaywall
